import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, View, Text, StyleSheet } from 'react-native';
import WebView from 'react-native-webview';
import { StatsAggReportService } from '../../../services/stats';
import { AccountPracInfoService, mergeAccountPracInfoByRequestedTypes } from '../../../services/account/AccountPracInfoService';
import { AccountPracInfoStorage } from '../../../storage/AccountPracInfoStorage';
import { wrapReportHtml, WEBVIEW_AUTO_HEIGHT_JS } from '../../../utils/reportRenderer';
import {
  buildAggDataLabelLookup,
  buildAggVarNameLookup,
  renderAggReportItems,
  type AggDataLabelLookup,
  type AggVarNameLookup,
} from '../../../utils/aggReportItemRenderer';
import { AdminAssessmentStorage } from '../../../services/AdminAssessmentStorage';
import type {
  AccountPracInfoResponseDto,
  AccountPracInfoType,
  AccountSiteDto,
  StatsAggReportDefDto,
  StatsAggRunResponseDto,
  StatsFilterDto,
} from '../../../types/api';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';
import { resolveAuthContext } from '../../../services/auth/authContext';
import { AGG_FILTER_CATALOG, ONLINE_SITE_LABEL, ONLINE_SITE_VALUE } from './aggReportConstants';
import { StatsDateInput } from './StatsDateInput';

interface AggReportViewProps {
  storage?: any;
  apiService?: any;
  accountId?: string | null;
  /** Preselect this report once defs load — set by ProGroupsStatsView.tsx after Setup saves,
   * so returning from editing/creating a report lands back on that same one. */
  initialReportId?: string;
  /** Bubbles the Setup button up to ProGroupsStatsView.tsx's local mode toggle — reportId is
   * the currently-selected report ('' if none) so Setup opens on the right one. */
  onOpenSetup: (reportId: string) => void;
}

const ALL_SITES_VALUE = '';

const parsePos = (pos: string | undefined): number => {
  const n = parseInt(pos ?? '', 10);
  return Number.isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
};

const resolveUserStore = (): any => resolveAuthContext().userStore || null;

/** Default runtime value for a filter catalog key when a report is first selected/loaded —
 * "all sites" for the site, blank (no bound) for the date range so the user sets it. */
const defaultFilterValue = (name: string): string => {
  switch (name) {
    case 'startDate':
    case 'endDate':
      return '';
    case 'site':
    default:
      return ALL_SITES_VALUE;
  }
};

export const AggReportView: React.FC<AggReportViewProps> = ({
  storage = null,
  apiService = null,
  accountId = null,
  initialReportId = '',
  onOpenSetup,
}) => {
  const [reportDefs, setReportDefs] = useState<StatsAggReportDefDto[]>([]);
  const [siteList, setSiteList] = useState<AccountSiteDto[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<string>('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [runResult, setRunResult] = useState<StatsAggRunResponseDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [webViewHeight, setWebViewHeight] = useState(0);

  const aggReportService = useMemo(
    () => (apiService ? new StatsAggReportService(apiService) : null),
    [apiService]
  );
  const accountPracInfoService = useMemo(
    () => (apiService ? new AccountPracInfoService(apiService) : null),
    [apiService]
  );

  // Data labels (StatsAggVarDto.dataLabels) come with the bundled assessment definitions — local
  // content, no network fetch. They replace raw data values in the rendered report.
  const [dataLabelLookup, setDataLabelLookup] = useState<AggDataLabelLookup>({});
  const [varNameLookup, setVarNameLookup] = useState<AggVarNameLookup>({});
  useEffect(() => {
    if (!storage) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const assessments = await new AdminAssessmentStorage(storage).getAvailableAssessments();
        if (!cancelled) {
          setDataLabelLookup(buildAggDataLabelLookup(assessments));
          setVarNameLookup(buildAggVarNameLookup(assessments));
        }
      } catch (labelError) {
        console.warn('AggReportView: failed to load assessment data labels', labelError);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storage]);

  // Load saved report definitions (AccountSettings.AggReportDefs) and the site list (needed by
  // the 'site' filter) together — cache-first, then a background refresh, same pattern as
  // UsageView's SiteList loading.
  useEffect(() => {
    let cancelled = false;

    const applyAccountData = (data: AccountPracInfoResponseDto | null | undefined) => {
      if (!data || cancelled) {
        return;
      }
      const defs = (data.AccountSettings?.AggReportDefs ?? []).filter((def) => def.Status !== '-1').sort((a, b) => parsePos(a.Pos) - parsePos(b.Pos));
      setReportDefs(defs);
      if (data.SiteList) {
        setSiteList(data.SiteList);
      }
    };

    const loadAccountData = async () => {
      if (!accountId) {
        return;
      }

      if (storage) {
        try {
          const pracStorage = new AccountPracInfoStorage(storage);
          const cached = await pracStorage.getAccountPracInfo(accountId);
          applyAccountData(cached);
        } catch (cacheError) {
          console.warn('AggReportView: failed to read cached account data', cacheError);
        }
      }

      if (!accountPracInfoService) {
        return;
      }

      try {
        const requestedTypes: AccountPracInfoType[] = ['AccountSettings', 'AccountSras'];
        const dto = await accountPracInfoService.getAccountPracInfo(
          { AccountId: accountId, Types: requestedTypes },
          { userStore: resolveUserStore(), storage }
        );

        if (cancelled) {
          return;
        }

        applyAccountData(dto);

        if (storage) {
          const pracStorage = new AccountPracInfoStorage(storage);
          const latestCached = await pracStorage.getAccountPracInfo(accountId);
          // Only overwrite the fields this request actually asked for (AccountSettings/
          // AccountSras) — a naive `{ ...latestCached, ...dto }` spread would also stomp
          // Touchpoints/BillPayInfo/AdminOptions/SraUser with the explicit `undefined`s
          // normalizeAccountPracInfoPayload leaves on an untouched field, wiping out whatever
          // ProGroupsAccountView's background prefetch had already loaded for Account.
          const merged = mergeAccountPracInfoByRequestedTypes(latestCached, dto, requestedTypes);
          await pracStorage.saveAccountPracInfo(accountId, merged);
        }
      } catch (fetchError) {
        console.warn('AggReportView: failed to refresh account data', fetchError);
      }
    };

    void loadAccountData();

    return () => {
      cancelled = true;
    };
  }, [accountId, accountPracInfoService, storage]);

  // Default to initialReportId (set by ProGroupsStatsView.tsx after Setup saves/creates a
  // report) once defs load, if nothing is selected yet — falling back to the first saved
  // report, or clearing selection if the previously-selected report no longer exists (e.g. it
  // was just deleted in Setup).
  useEffect(() => {
    if (reportDefs.length === 0) {
      setSelectedReportId('');
      return;
    }
    if (!reportDefs.some((def) => def.ReportId === selectedReportId)) {
      const preferred = reportDefs.find((def) => def.ReportId === initialReportId);
      setSelectedReportId(preferred?.ReportId ?? reportDefs[0].ReportId);
    }
  }, [reportDefs, selectedReportId, initialReportId]);

  const selectedDef = useMemo(
    () => reportDefs.find((def) => def.ReportId === selectedReportId) ?? null,
    [reportDefs, selectedReportId]
  );

  const runReport = useCallback(async (reportId: string, values: Record<string, string>) => {
    if (!accountId || !reportId) {
      return;
    }
    if (!aggReportService) {
      setError('Aggregate report service unavailable.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const filters: StatsFilterDto[] = Object.entries(values).map(([Name, Value]) => ({ Name, Value }));
      const response = await aggReportService.runReport(
        { AccountId: accountId, ReportId: reportId, Filters: filters },
        { userStore: resolveUserStore(), storage }
      );
      setRunResult(response);
    } catch (fetchError) {
      const message = fetchError instanceof Error ? fetchError.message : String(fetchError);
      setError(message);
      setRunResult(null);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  }, [accountId, aggReportService, storage]);

  // Re-seeds filter values to their defaults and runs the report whenever the selected report
  // changes (including the initial load) — an old filter value from a different report
  // shouldn't carry over. Deliberately keyed only on selectedDef, not filterValues: individual
  // filter changes and Refresh (below) call runReport directly with the latest values, so
  // watching filterValues here too would double-fetch on every dropdown change.
  useEffect(() => {
    if (!selectedDef) {
      setFilterValues({});
      return;
    }
    const next: Record<string, string> = {};
    selectedDef.Filters.forEach((filter) => {
      next[filter.Name] = defaultFilterValue(filter.Name);
    });
    setFilterValues(next);
    void runReport(selectedDef.ReportId, next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDef]);

  const handleFilterChange = (name: string, value: string) => {
    const next = { ...filterValues, [name]: value };
    setFilterValues(next);
    if (selectedDef) {
      void runReport(selectedDef.ReportId, next);
    }
  };

  const handleRefresh = () => {
    if (selectedDef) {
      void runReport(selectedDef.ReportId, filterValues);
    }
  };

  const isWeb = Platform.OS === 'web';
  const webSelectStyle = { ...webControlStyle, minWidth: 150 } as any;

  const siteOptions = useMemo(
    () => siteList
      .map((site) => ({ value: (site.SiteId || '').trim(), label: (site.SiteDName || '').trim() || site.SiteId }))
      .filter((option) => !!option.value),
    [siteList]
  );

  const cycleSelectValue = (options: string[], current: string, onChange: (value: string) => void) => {
    const currentIndex = options.indexOf(current);
    const nextIndex = currentIndex < 0 || currentIndex === options.length - 1 ? 0 : currentIndex + 1;
    onChange(options[nextIndex]);
  };

  const renderFilterControl = (filterName: string) => {
    const value = filterValues[filterName] ?? defaultFilterValue(filterName);

    if (filterName === 'site') {
      const options = [
        { value: ALL_SITES_VALUE, label: 'All Sites' },
        { value: ONLINE_SITE_VALUE, label: ONLINE_SITE_LABEL },
        ...siteOptions,
      ];
      return (
        <View style={styles.filterGroup} key={filterName}>
          <Text style={styles.filterLabel}>Site</Text>
          {isWeb ? (
            <select
              style={webSelectStyle}
              value={value}
              onChange={(event: any) => handleFilterChange('site', String(event?.target?.value ?? ALL_SITES_VALUE))}
              aria-label="Site"
            >
              {options.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          ) : (
            <Pressable
              style={styles.dropdownBtn}
              onPress={() => cycleSelectValue(options.map((o) => o.value), value, (next) => handleFilterChange('site', next))}
              accessibilityRole="button"
              accessibilityLabel="Site"
            >
              <Text style={styles.dropdownBtnText}>{options.find((o) => o.value === value)?.label ?? 'All Sites'}</Text>
              <Text style={styles.dropdownBtnChevron}>▼</Text>
            </Pressable>
          )}
        </View>
      );
    }

    if (filterName === 'startDate' || filterName === 'endDate') {
      const label = filterName === 'startDate' ? 'Start Date' : 'End Date';
      return (
        <View style={styles.filterGroup} key={filterName}>
          <Text style={styles.filterLabel}>{label}</Text>
          <StatsDateInput
            value={value}
            onChange={(next) => handleFilterChange(filterName, next)}
            ariaLabel={label}
          />
        </View>
      );
    }

    return null;
  };

  const resultsHtml = useMemo(() => {
    if (!selectedDef) {
      return '';
    }
    return wrapReportHtml(renderAggReportItems(selectedDef.Items, runResult?.Items ?? [], dataLabelLookup, varNameLookup));
  }, [selectedDef, runResult, dataLabelLookup, varNameLookup]);

  return (
    <View style={styles.panel}>
      <View style={styles.filterBarRow}>
        <View style={styles.setupBar}>
          <Text style={styles.filterLabel}>Report</Text>
          {isWeb ? (
            <select
              style={{ ...webSelectStyle, minWidth: 200 }}
              value={selectedReportId}
              onChange={(event: any) => setSelectedReportId(String(event?.target?.value ?? ''))}
              aria-label="Report"
            >
              {reportDefs.length === 0 ? <option value="">No reports configured</option> : null}
              {reportDefs.map((def) => (
                <option key={def.ReportId} value={def.ReportId}>{def.DName || def.ReportId}</option>
              ))}
            </select>
          ) : (
            <Pressable
              style={[styles.dropdownBtn, { minWidth: 200 }]}
              onPress={() => cycleSelectValue(reportDefs.map((d) => d.ReportId), selectedReportId, setSelectedReportId)}
              accessibilityRole="button"
              accessibilityLabel="Report"
            >
              <Text style={styles.dropdownBtnText}>
                {reportDefs.find((d) => d.ReportId === selectedReportId)?.DName || 'No reports configured'}
              </Text>
              <Text style={styles.dropdownBtnChevron}>▼</Text>
            </Pressable>
          )}

          <View style={styles.filterSpacer} />

          <Pressable
            style={styles.refreshButton}
            onPress={() => onOpenSetup(selectedReportId)}
            accessibilityRole="button"
            accessibilityLabel="Setup"
          >
            <Text style={styles.refreshButtonText}>Setup</Text>
          </Pressable>
        </View>
      </View>

      {selectedDef && selectedDef.Filters.length > 0 ? (
        <View style={styles.filterBarRow}>
          <View style={styles.filterBar}>
            {[...selectedDef.Filters]
              .sort((a, b) => (parseInt(a.Pos, 10) || 0) - (parseInt(b.Pos, 10) || 0))
              .filter((filter) => AGG_FILTER_CATALOG.some((entry) => entry.name === filter.Name))
              .map((filter) => renderFilterControl(filter.Name))}

            <View style={styles.filterSpacer} />

            <Pressable
              style={[styles.refreshButton, isLoading && styles.refreshButtonDisabled]}
              onPress={handleRefresh}
              disabled={isLoading}
              accessibilityRole="button"
              accessibilityLabel="Refresh"
            >
              <Text style={styles.refreshButtonText}>{isLoading ? 'Refreshing…' : 'Refresh'}</Text>
            </Pressable>
          </View>
        </View>
      ) : null}

      <View style={styles.mainCardRow}>
        <View style={styles.mainCard}>
          {error ? (
            <View style={styles.errorCard}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          ) : null}

          {!selectedDef ? (
            <View style={styles.placeholderRow}>
              <Text style={styles.placeholderText}>
                {reportDefs.length === 0
                  ? 'No aggregate reports configured yet. Press Setup to create one.'
                  : 'Select a report above to view its results.'}
              </Text>
            </View>
          ) : !hasLoaded && isLoading ? (
            <View style={styles.placeholderRow}>
              <Text style={styles.placeholderText}>Loading report…</Text>
            </View>
          ) : (
            // The WebView is sized to its full content height, so the card scrolls vertically
            // instead of the report being cut off at the bottom of the viewport.
            <ScrollView style={styles.resultsScroll} contentContainerStyle={styles.resultsScrollContent}>
              <WebView
                source={{ html: resultsHtml }}
                style={[styles.resultsWebView, { height: webViewHeight || 0 }]}
                scrollEnabled={false}
                injectedJavaScript={WEBVIEW_AUTO_HEIGHT_JS}
                onMessage={(event: any) => setWebViewHeight(Number(event?.nativeEvent?.data) || 0)}
              />
            </ScrollView>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  panel: {
    flex: 1,
    minHeight: 0,
    padding: 20,
    backgroundColor: EDITOR_COLORS.background,
  },
  filterBarRow: {
    width: '100%',
    alignItems: 'center',
    marginBottom: 16,
  },
  setupBar: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: EDITOR_COLORS.background,
  },
  filterBar: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    flexWrap: 'wrap',
    justifyContent: 'center',
    rowGap: 10,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    backgroundColor: EDITOR_COLORS.background,
  },
  filterGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 20,
  },
  filterLabel: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    marginRight: 8,
  },
  filterSpacer: {
    width: 20,
  },
  dropdownBtn: {
    height: 28,
    minWidth: 150,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: EDITOR_COLORS.background,
  },
  dropdownBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    flex: 1,
  },
  dropdownBtnChevron: {
    ...editorText,
    fontSize: 11,
    color: EDITOR_COLORS.textMuted,
    marginLeft: 4,
  },
  refreshButton: {
    height: 28,
    paddingHorizontal: 16,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.submitBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  refreshButtonDisabled: {
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
  },
  refreshButtonText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  mainCardRow: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    alignItems: 'center',
  },
  mainCard: {
    width: '100%',
    maxWidth: 700,
    flex: 1,
    minHeight: 0,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.separator,
    borderRadius: 8,
    backgroundColor: EDITOR_COLORS.background,
    overflow: 'hidden',
  },
  resultsScroll: {
    flex: 1,
  },
  resultsScrollContent: {
    padding: 12,
  },
  errorCard: {
    backgroundColor: EDITOR_COLORS.errorBackground,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  errorText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    lineHeight: 20,
  },
  resultsWebView: {
    backgroundColor: 'transparent',
  },
  placeholderRow: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  placeholderText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    textAlign: 'center',
  },
});
